import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

const Sidebar = ({ isMobileMenuOpen, onMobileMenuToggle }) => {
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState({
        clinics: false
    });

    const isActive = (path) => {
        return location.pathname === path;
    };

    const isParentActive = (parentPath) => {
        return location.pathname.startsWith(parentPath);
    };

    const toggleExpand = (key) => {
        setExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSubItemClick = (key) => {
        onMobileMenuToggle(); // Close mobile menu if open
        setExpandedItems(prev => ({ ...prev, [key]: false })); // Close dropdown
    };

    const navItems = [
        {
            title: 'Dashboard',
            path: '/dashboard',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
            )
        },
        {
            title: 'Clinic Management',
            path: '/clinics',
            key: 'clinics',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            subItems: [
                { title: 'Onboard Clinics', path: '/clinics/onboard' },
                { title: 'Manage Clinics', path: '/clinics/manage' }
            ]
        },
        {
            title: 'User Management',
            path: '/user-management',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            )
        }
    ];

    return (
        <>
            {/* Mobile overlay */}
            {isMobileMenuOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={onMobileMenuToggle}
                />
            )}

            {/* Sidebar */}
            <div className={`
                bg-gray-800 text-white min-h-screen px-4 py-6 transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                lg:translate-x-0 lg:relative lg:w-64
                fixed top-0 left-0 w-64 z-50 lg:z-auto
            `}>
                <div className="mb-8">
                    <h1 className="text-xl lg:text-2xl font-bold">HairQure Admin</h1>
                </div>
                
                <nav>
                    {navItems.map((item) => (
                        <div key={item.path} className="mb-4">
                            {item.subItems ? (
                                // Parent item with dropdown
                                <div>
                                    <button
                                        onClick={() => toggleExpand(item.key)}
                                        className={`flex items-center justify-between w-full p-2 rounded-lg hover:bg-gray-700 transition-colors ${
                                            isParentActive(item.path) ? 'bg-gray-700' : ''
                                        }`}
                                    >
                                        <div className="flex items-center space-x-2">
                                            {item.icon}
                                            <span className="text-sm lg:text-base">{item.title}</span>
                                        </div>
                                        {expandedItems[item.key] ? (
                                            <ChevronUpIcon className="w-4 h-4" />
                                        ) : (
                                            <ChevronDownIcon className="w-4 h-4" />
                                        )}
                                    </button>
                                    
                                    {/* Dropdown items */}
                                    <div className={`ml-6 mt-2 space-y-2 transition-all duration-200 ${
                                        expandedItems[item.key] ? 'block' : 'hidden'
                                    }`}>
                                        {item.subItems.map((subItem) => (
                                            <Link
                                                key={subItem.path}
                                                to={subItem.path}
                                                className={`block p-2 rounded-lg hover:bg-gray-700 transition-colors text-sm ${
                                                    isActive(subItem.path) ? 'bg-gray-700' : ''
                                                }`}
                                                onClick={() => handleSubItemClick(item.key)}
                                            >
                                                {subItem.title}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                // Regular menu item
                                <Link
                                    to={item.path}
                                    className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-700 transition-colors ${
                                        isActive(item.path) ? 'bg-gray-700' : ''
                                    }`}
                                    onClick={onMobileMenuToggle}
                                >
                                    {item.icon}
                                    <span className="text-sm lg:text-base">{item.title}</span>
                                </Link>
                            )}
                        </div>
                    ))}
                </nav>
            </div>
        </>
    );
};

export default Sidebar; 